import { Route } from '@angular/router';
import { InitialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { LanguageRouteGuard } from 'app/core/i18n/language-route.guard';

// Public routes that are duplicated under each language prefix
const publicChildren: Route[] = [
    {
        path: '',
        pathMatch: 'full',
        loadChildren: () =>
            import('app/modules/landing/home/home.module').then(
                (m) => m.LandingHomeModule
            ),
    },
    {
        path: 'home',
        loadChildren: () =>
            import('app/modules/landing/home/home.module').then(
                (m) => m.LandingHomeModule
            ),
    },
    {
        path: 'demo',
        loadChildren: () =>
            import('app/modules/demo/demo.module').then(
                (m) => m.DemoModule
            ),
    },
    {
        path: 'policy',
        loadChildren: () =>
            import('app/modules/policies/policies.module').then(
                (m) => m.PoliciesModule
            ),
    },
    {
        path: 'pricing',
        loadChildren: () =>
            import('app/modules/pricing/pricing.module').then(
                (m) => m.PricingModule
            ),
    },
    {
        path: 'sign-in',
        loadChildren: () =>
            import('app/modules/auth/sign-in/sign-in.module').then(
                (m) => m.AuthSignInModule
            ),
    },
    {
        path: 'sign-up',
        loadChildren: () =>
            import('app/modules/auth/sign-up/sign-up.module').then(
                (m) => m.AuthSignUpModule
            ),
    },
    {
        path: 'confirmation-required',
        loadChildren: () =>
            import(
                'app/modules/auth/confirmation-required/confirmation-required.module'
            ).then((m) => m.AuthConfirmationRequiredModule),
    },
    {
        path: 'forgot-password',
        loadChildren: () =>
            import(
                'app/modules/auth/forgot-password/forgot-password.module'
            ).then((m) => m.AuthForgotPasswordModule),
    },
    {
        path: 'reset-password',
        loadChildren: () =>
            import(
                'app/modules/auth/reset-password/reset-password.module'
            ).then((m) => m.AuthResetPasswordModule),
    },
    {
        path: 'sign-out',
        loadChildren: () =>
            import('app/modules/auth/sign-out/sign-out.module').then(
                (m) => m.AuthSignOutModule
            ),
    },
];

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect root to default language
    { path: '', pathMatch: 'full', redirectTo: 'nl' },

    // Redirect signed in user after login
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'app/profile' },

    // Legacy redirects (old routes → new /app/* routes)
    { path: 'profile', redirectTo: 'app/profile', pathMatch: 'prefix' },
    { path: 'gym', redirectTo: 'app/gym', pathMatch: 'prefix' },
    { path: 'contacts', redirectTo: 'app/contacts', pathMatch: 'prefix' },

    // Language-prefixed public routes
    {
        path: ':lang',
        canActivate: [LanguageRouteGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: publicChildren,
    },

    // Auth routes for authenticated users (unlock-session)
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'unlock-session',
                loadChildren: () =>
                    import(
                        'app/modules/auth/unlock-session/unlock-session.module'
                    ).then((m) => m.AuthUnlockSessionModule),
            },
        ],
    },

    // App routes (auth required) - all under /app prefix
    {
        path: 'app',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'gym',
                loadChildren: () =>
                    import('app/modules/gym/gym.module').then(
                        (m) => m.GymModule
                    ),
            },
            {
                path: 'profile',
                loadChildren: () =>
                    import('app/modules/profile/profile.module').then(
                        (m) => m.ProfileModule
                    ),
            },
            {
                path: 'contacts',
                loadChildren: () =>
                    import('app/modules/contacts/contacts.module').then(
                        (m) => m.ContactsModule
                    ),
            },
            // Default redirect within /app
            { path: '', redirectTo: 'profile', pathMatch: 'full' },
        ],
    },

    // Catch-all wildcard route - must be last
    { path: '**', redirectTo: '/nl' },
];
